import dayjs from 'dayjs';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { InstantSearch, useQueryRules } from 'react-instantsearch';
import { ThemeProvider } from 'styled-components';

import useMemoizedFn from '@/utils/useMemoizedFn';

import { Configure } from '@/components/Configure';
import {
  applyProductConfigurationFilters,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { retailerWidgetAlgoliaClient } from '@/integrations/algolia/retailerWidgetClient';
import type { StrapHit, WatchHit } from '@/integrations/algolia/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { useRetailerPageContext } from '@/providers/RetailerPageProvider/RetailerPageProviderContext';
import { algoliaState } from '@/states/algoliaState';
import { GlobalStyle, theme } from '@/theme';

import { scrollToTop } from './components/RetailerContactUs/helpers';
import { RetailerNavbar } from './components/RetailerNavbar/RetailerNavbar';
import { RetailerProductDetail } from './components/RetailerProductDetail/RetailerProductDetail';
import { RetailerProductList } from './components/RetailerProductList/RetailerProductList';

import * as S from './RetailerWidget.styles';

interface RetailerWidgetConfig {
  countryCode: string;
  defaultLocale?: string;
  enabled?: boolean;
  locales?: string[];
  url?: string;
}

export interface RetailerWidgetProps {
  locale?: string;
  multiDoors?: string[] | null;
  retailerId: string;
}

export const RetailerWidget: FC<RetailerWidgetProps> = ({
  locale = 'us-en',
  multiDoors,
  retailerId,
}) => {
  const { country: routeCountry } = useRegionProvider();
  const { retailerPage, selectedProduct, setRetailerPage, setSelectedProduct } =
    useRetailerPageContext();
  const [widgetConfig, setWidgetConfig] = useState<RetailerWidgetConfig | null>(
    null,
  );
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const indexName = getIndexName('PRODUCT', locale);

  const sixMonthsAgo =
    Math.floor(dayjs().utc().startOf('date').toDate().getTime() / 1000) -
    180 * 24 * 60 * 60;

  let algoliaFilters;

  if (retailerPage === 'new') {
    algoliaFilters = `launch_date >= ${sixMonthsAgo}`;
    algoliaFilters = applyProductConfigurationFilters(
      ProductConfigurationFilterType.WIDGET,
      'Watch',
      false,
      algoliaFilters,
      true,
    );
  } else {
    algoliaFilters = `(widget_stock_in_wholesaler_ids:${retailerId} OR widget_is_top_range:true)`;

    algoliaFilters = applyProductConfigurationFilters(
      ProductConfigurationFilterType.WIDGET,
      'Watch',
      false,
      algoliaFilters,
      true,
    );
  }

  // Fetch widget configuration data only on client-side
  useEffect(() => {
    // Skip server-side execution
    if (!isClient) return;

    const fetchWidgetConfig = async () => {
      try {
        const posManagerUrl = process.env.NEXT_PUBLIC_POS_MANAGER_APP_API_URI;

        if (!posManagerUrl) {
          console.error(
            'POS Manager URL is not defined in environment variables',
          );
          return;
        }

        const query = `
          query GetRetailerWidgetConfig($externalReference: String!) {
            locations(filter: { externalReference: $externalReference }) {
              edges {
                node {
                  id
                  name
                  externalReference
                  offersAppointment
                  widget {
                    enabled
                    url
                    locales
                    defaultLocale
                    code
                    countryCode
                    access {
                      count
                      firstAccessDate
                      lastAccessDate
                    }
                  }
                }
              }
            }
          }
        `;

        const variables = {
          externalReference: retailerId,
        };

        const response = await fetch(posManagerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.data?.locations?.edges?.length > 0) {
          const responseWidgetConfig = data.data.locations.edges[0].node.widget;

          // Ensure locales is always an array
          const processedConfig = {
            ...responseWidgetConfig,
            locales: Array.isArray(responseWidgetConfig?.locales)
              ? responseWidgetConfig.locales
              : responseWidgetConfig?.locales
              ? [responseWidgetConfig.locales]
              : [],
          };

          setWidgetConfig(processedConfig);
        } else {
          setWidgetConfig({
            enabled: true,
            locales: ['EN', 'FR', 'ES', 'DE'],
            defaultLocale: 'EN',
            countryCode: routeCountry.code ?? 'US',
          });
        }
      } catch (error) {
        console.error('Error fetching widget configuration:', error);
        setWidgetConfig({
          enabled: true,
          locales: ['EN', 'FR', 'ES', 'DE'],
          defaultLocale: 'EN',
          countryCode: routeCountry.code ?? 'US',
        });
      }
    };

    if (retailerId) {
      fetchWidgetConfig();
    }
  }, [retailerId, isClient]);

  const handleProductSelect = (product: WatchHit | StrapHit) => {
    const productWithRetailer = {
      ...product,
      retailerId: retailerId,
    };

    Promise.all([setSelectedProduct(productWithRetailer)]).then(() => {
      // Double call to requestAnimationFrame is needed when we are scrolled all the way to the bottom of the page
      // Otherwise the scroll is not working (problem with how the browser renders the page and the scrollTo is executed)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToTop();
        });
      });
    });
  };

  const handleBackToList = () => {
    setSelectedProduct(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <>
        <GlobalStyle />
        <S.RetailerWidgetContainer className='retailer-widget'>
          <RetailerNavbar
            activeTab={retailerPage}
            onBackClick={handleBackToList}
            onTabChange={(tab) => {
              setRetailerPage(tab);
              if (selectedProduct) {
                setSelectedProduct(null);
              }
            }}
            showBackButton={!!selectedProduct}
            widgetConfig={widgetConfig || undefined}
          />

          <InstantSearch
            key={retailerPage}
            indexName={indexName}
            insights={true}
            searchClient={retailerWidgetAlgoliaClient}
          >
            <Configure
              analyticsTags={[
                'platform:web',
                'product:watches',
                'context:retailer-widget',
                `retailer:${retailerId}`,
              ]}
              distinct={false}
              filters={algoliaFilters}
              hitsPerPage={20}
              ruleContexts={['retailer-top-range']}
              userToken='retailer-widget'
            />
            <CustomQueryRuleContext />

            {selectedProduct ? (
              <RetailerProductDetail
                multiDoors={multiDoors}
                product={selectedProduct}
                retailerId={retailerId}
              />
            ) : (
              <RetailerProductList onProductSelect={handleProductSelect} />
            )}
          </InstantSearch>
        </S.RetailerWidgetContainer>
      </>
    </ThemeProvider>
  );
};

function CustomQueryRuleContext() {
  // can not use useSortBy to get currentRefinement, the transformRuleContexts will be executed fast then useSortBy's currentRefinement
  const transformRuleContexts = useMemoizedFn((rules: string[]) => {
    const indexName: string = algoliaState.sortBy ?? '';
    if (indexName.includes('price_desc') || indexName.includes('price_asc')) {
      return rules.filter((item) => item !== 'retailer-top-range');
    }
    return rules;
  });
  const returnAll = useMemoizedFn((values: any) => {
    return values;
  });

  useQueryRules({
    trackedFilters: {
      // must set trackedFilters, otherwise transformRuleContexts will not be triggered, wired bug
      custom: returnAll,
    },
    transformRuleContexts: transformRuleContexts,
  });
  return null;
}
